<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" hide-details />
            </template>
            <template #default>
              <v-row>
                <v-col sm="5">
                  <select-list
                    v-model="query.params.status"
                    :items="statusOptions"
                    label="Status"
                    clearable
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <data-table
                :headers="tableHeaders"
                :items="partners"
                :items-per-page="pagination.perPage"
                :page="pagination.currentPage"
                :server-items-length="pagination.total"
                :route="{ name: 'partners.edit', params: ['slug'] }"
                :footer-props="{ 'items-per-page-options': perPageOptions }"
                disable-sort
                @pagination="$_queryHandler_onUpdatePagination"
              >
                <template #item="{ item }">
                  <td>
                    <div class="d-flex align-center">
                      <item-logo :name="item.name" :src="item.logoThumbnail" class="mr-4" />
                      <div>
                        <span>{{ item.name }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="text-left">
                    <base-chip v-if="showAsArchived" icon="archive" text="Archived" />
                    <published-chip v-else :value="item.published" />
                  </td>
                </template>
                <template #actions="{ item: item }">
                  <v-list-item :disabled="!isPermittedTo('delete')" @click="onClickDelete(item)">
                    <v-list-item-title>
                      <v-icon left small>delete</v-icon>
                      <span>Delete</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>
    <fab-actions
      :can-edit="isPermittedTo('update')"
      context="Partners"
      @create="toggleCreateModal"
      @export="onClickExport"
    />
    <generic-modal
      v-model="showCreateModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      confirm-text="Create Partner"
      title="Create a partner"
      persistent
      @modal-confirm="onConfirmCreate"
    >
      <div v-if="hasErrors" class="error--text">Unable to create partner. Please try again</div>
      <text-input
        v-model="newPartner"
        :disabled="isLoading"
        :rules="rules"
        class="mt-5"
        counter
        label="Partner Name"
        maxlength="25"
      />
    </generic-modal>
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import FabActions from '@/components/fab-actions'
import TextInput from '@/components/forms/text-input'
import SelectList from '@/components/forms/select-list'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import FilterAccordion from '@/components/filter-accordion'
import DataTable from '@/components/data-table'
import ItemLogo from '@/components/item-logo'
import PublishedChip from '@/components/chips/published'
import BaseChip from '@/components/chips/base-chip'

import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { partners } from '@/services/offers'

import { exportCSV } from '@/utils/csv'

export default {
  name: 'PartnerManagement',
  components: {
    FabActions,
    GenericModal,
    PageLoader,
    SearchInput,
    TextInput,
    FilterAccordion,
    DataTable,
    ItemLogo,
    PublishedChip,
    BaseChip,
    SelectList,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    hasErrors: false,
    isLoading: false,
    queriesDefined: ['status'],
    searchKey: 'name',
    partners: [],
    newPartner: '',
    rules: [
      value => !!value || 'A name is required',
      value => (value && value.length >= 3) || 'Partner name must be a minimum of 3 characters',
    ],
    showAsArchived: false,
    showCreateModal: false,
    showExportModal: false,
    perPageOptions: [15, 30, 50, -1],
    pagination: {
      perPage: 30,
    },
    tableHeaders: [
      { text: 'Partner', value: 'name' },
      { text: 'Status', value: 'published', align: 'left' },
    ],
    tempPublishedStatus: false,
    statusOptions: [
      { key: 'Published Only', value: 'published' },
      { key: 'Archived Only', value: 'archived' },
    ],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['options', 'hasOptions']),
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getPartners() {
      this.isLoading = true
      const { perPage, currentPage: page } = this.pagination

      try {
        const { data, pagination: { total } = {} } = await partners.getAllPartners({
          compact: true,
          page,
          perPage: perPage === -1 ? null : perPage,
          filters: this.queryFilters,
        })
        this.partners = data || []
        this.pagination.total = total
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve partners',
        })
      }

      this.setPageLoading(false)
      this.isLoading = false
      this.showAsArchived = Boolean(this.query.params.status === 'archived')
    },
    async onConfirmCreate() {
      this.showCreateModal = false
      if (!this.newPartner) return
      this.hasErrors = false
      this.isLoading = true

      try {
        const { slug } = await partners.createPartner(this.newPartner)
        this.$router.push({ name: 'partners.edit', params: { slug } })
      } catch (error) {
        this.hasErrors = true
        this.$notify({
          type: 'error',
          text: 'Unable to create new partner',
        })
      }

      this.isLoading = false
    },
    async handleQueryUpdate() {
      await this.getPartners()
    },
    async onClickExport() {
      this.isLoading = true
      const { data: filteredPartners } = await partners.getAllPartners({
        compact: true,
        filters: this.queryFilters,
      })
      const headers = ['name', 'slug', 'status'].join(',')
      const body = filteredPartners.map(({ name, published, slug }) =>
        [name, slug, published ? 'published' : 'draft'].join(',')
      )

      const data = [headers].concat(body).join('\n')

      exportCSV(data, 'partners-export')

      this.isLoading = false
    },
    toggleCreateModal() {
      this.showCreateModal = !this.showCreateModal
    },
  },
}
</script>
